import { useEffect, useState } from "react";
import Input from "../../components/form/Input";
import Button from "../../components/form/Button";
import Image from "../../components/elements/Image";
import ButtonFile from "../../components/form/ButtonFile";
import { siteSettingAddEdit } from "../../services/siteSettingService";
import useForm from "../../hooks/useForm";
import { findEmptyIndexes, validateImage } from "../../util/Flag";
import useMutation from "../../hooks/useMutation";
import { useSelector } from "react-redux";
import Textarea from "../../components/form/Textarea";
import { useDispatch } from "react-redux";
import { getSettings } from "../../redux/slice/settingsSlice";

export default function SiteSettings() {
  const [siteSettingData, setSiteSettingData] = useState(null);
  const { siteSetting } = useSelector((state) => state.setting);
  const [error, setError] = useState({
    errorIndex: [],
    isError: false,
    errorMessage: "",
  });
  const dispatch = useDispatch();

  const getSocialMediaDetails = (url) => {
    try {
      const domain = new URL(url).hostname.replace("www.", "");

      // Known platforms with icons
      const socialPlatforms = {
        "linkedin.com": { name: "LinkedIn", icon: "fa-brands fa-linkedin" },
        "facebook.com": { name: "Facebook", icon: "fa-brands fa-facebook" },
        "twitter.com": { name: "Twitter", icon: "fa-brands fa-x-twitter" },
        "x.com": { name: "Twitter", icon: "fa-brands fa-x-twitter" },
        "instagram.com": { name: "Instagram", icon: "fa-brands fa-instagram" },
        "youtube.com": { name: "YouTube", icon: "fa-brands fa-youtube" },
        "tiktok.com": { name: "TikTok", icon: "fa-brands fa-tiktok" },
        "github.com": { name: "GitHub", icon: "fa-brands fa-github" },
        "reddit.com": { name: "Reddit", icon: "fa-brands fa-reddit" },
        "whatsapp.com": { name: "WhatsApp", icon: "fa-brands fa-whatsapp" },
      };

      if (socialPlatforms[domain]) {
        return socialPlatforms[domain]; // Return predefined name & icon
      }

      // If domain is not in the list, use it as a fallback
      return {
        name: domain.charAt(0).toUpperCase() + domain.slice(1),
        icon: "fa-solid fa-globe",
      };
    } catch (error) {
      return { name: "", icon: "" }; // In case of invalid URL
    }
  };

  useEffect(() => {
    if (siteSetting) {
      setSiteSettingData(siteSetting);
    }
  }, [siteSetting]);

  const validation = {
    title: { required: true, message: "Title is required" },
  };

  useEffect(() => {
    if (siteSettingData?._id) {
      const {
        title,
        address,
        officeHours,
        email,
        social,
        footerTitle,
        logo,
        favicon,
        description,
      } = siteSettingData;
      setFieldsValue({
        title,
        address,
        officeHours,
        email,
        social,
        logo: logo?.url,
        favicon: favicon?.url,
        footerTitle,
        description,
      });
    }
  }, [siteSettingData?._id]);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      title: "",
      address: "",
      description: "",
      officeHours: "",
      email: "",
      logo: "",
      favicon: "",
      footerTitle: "",
      social: [],
    },
  });

  const {
    mutate,
    error: sumbitError,
    isPending: isLoad,
  } = useMutation({
    mutationFn: async (val) => {
      if (val?.social.every((item) => item?.url)) {
        return await siteSettingAddEdit({
          ...val,
          ...(siteSettingData?._id && { id: siteSettingData?._id }),
        });
      } else {
        const emptyIndexes = findEmptyIndexes(val?.social);
        setError((prev) => ({
          ...prev,
          isError: true,
          errorIndex: emptyIndexes,
          errorMessage: "Please Fill Any Social Media",
        }));
      }
    },
    onSuccess: (res) => {
      dispatch(getSettings(res?.data));
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Site Settings
        </div>

        <form onSubmit={(event) => handleSubmit(event, mutate)}>
          <div className="flex flex-col-reverse lg:flex-row xls:grid xls:grid-cols-12 bg-white border border-shades-05/20 rounded-xl">
            {/* Left Side Form Inputs */}
            <div className="col-span-12 xls:col-span-8 space-y-10 border-b xl:border-b-0 lg:border-r border-shades-05/25 py-6 px-4 sm:px-6 xl:px-10 lg:max-w-[700px] lg:w-full xls:max-w-[100%]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative col-span-full">
                  <Input
                    label="Title"
                    inputPlaceholder="Enter Title"
                    inputName="title"
                    value={values?.title}
                    onChange={handleChange}
                    {...formValidate(errors, "title")}
                  />
                </div>
                <div className="relative col-span-full">
                  <Input
                    label="Address"
                    inputPlaceholder="Enter Address"
                    inputName="address"
                    value={values?.address}
                    onChange={handleChange}
                    {...formValidate(errors, "address")}
                  />
                </div>
                <div className="relative">
                  <Input
                    label="Office Hours"
                    inputPlaceholder="Enter Date and Time"
                    inputName="officeHours"
                    value={values?.officeHours}
                    onChange={handleChange}
                    {...formValidate(errors, "officeHours")}
                  />
                </div>
                <div className="relative">
                  <Input
                    label="Email"
                    inputPlaceholder="Enter Email"
                    inputName="email"
                    value={values?.email}
                    onChange={handleChange}
                    {...formValidate(errors, "email")}
                  />
                </div>
                <div className="relative col-span-full">
                  <Input
                    label="Footer Title"
                    inputPlaceholder="Enter Footer Title"
                    inputName="footerTitle"
                    value={values?.footerTitle}
                    onChange={handleChange}
                    {...formValidate(errors, "footerTitle")}
                  />
                </div>
                <div className="relative col-span-full">
                  <Textarea
                    label="Footer Description"
                    inputPlaceholder="Enter Footer Description"
                    inputName="description"
                    inputValue={values?.description}
                    onChange={handleChange}
                    {...formValidate(errors, "description")}
                  />
                </div>
              </div>

              {/* Social Media Links */}
              <div className="relative space-y-3">
                <div className="font-CanelaDeck font-medium pb-2 border-b border-shades-04">
                  Social Media Links
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {values?.social?.map((platform, index) => (
                    <div key={index} className="flex items-center">
                      {platform?.icon && (
                        <div className="w-[50px] aspect-square flex items-center justify-center mt-5 border border-shades-05/50 rounded-l-md -mr-1">
                          <i className={`${platform?.icon}`}></i>
                        </div>
                      )}
                      <Input
                        label={platform.name || "Link"}
                        inputName={`social-${index}`}
                        inputPlaceholder={`Enter ${platform.name} link like https://facebook.com/`}
                        inputClasses={platform?.icon ? "!rounded-l-none" : ""}
                        labelClasses={platform?.icon ? "-ml-9" : ""}
                        value={platform?.url}
                        onChange={(e) => {
                          const url = e.target.value;
                          const { name, icon } = getSocialMediaDetails(url);
                          const updatedSocial = [...values.social];
                          updatedSocial[index] = {
                            ...updatedSocial[index],
                            url,
                            name,
                            icon,
                          };
                          setFieldsValue({ social: updatedSocial });
                          if (url.trim() !== "") {
                            setError((prev) => ({
                              ...prev,
                              errorIndex: prev.errorIndex?.filter(
                                (idx) => idx !== index
                              ),
                            }));
                          }
                        }}
                        errorType={
                          error.isError &&
                          error.errorIndex.includes(index) &&
                          "warning"
                        }
                        errorText={
                          error.errorIndex.includes(index) && error.errorMessage
                        }
                      />
                      <Button
                        type="button"
                        buttonIcon="fa-regular fa-trash"
                        buttonIconPosition="left"
                        buttonClasses="text-white mt-5 mx-3"
                        buttonFunction={() => {
                          const updatedSocial = values.social.filter(
                            (_, idx) => idx !== index
                          );
                          setFieldsValue({ social: updatedSocial || [] });
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex">
                  <Button
                    type="button"
                    buttonLabel="Add More"
                    buttonClasses="!text-chestnut-rose !h-6 !p-0 !rounded-none !bg-transparent"
                    buttonLabelClasses="!text-sm"
                    buttonFunction={() => {
                      setFieldsValue({
                        social: [
                          ...values.social,
                          { name: "", url: "", icon: "" },
                        ],
                      });
                    }}
                  />
                </div>
              </div>

              <div className="flex">
                <Button
                  buttonLabel="Save Settings"
                  buttonClasses="!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
                  buttonLabelClasses="mx-auto"
                  buttonType="submit"
                  {...(isLoad && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            </div>

            {/* Right Side Logo + Favicon */}
            <div className="col-span-12 xls:col-span-4 py-6 px-4 sm:px-6 xl:px-10 lg:space-y-10 flex flex-col md:flex-row lg:grid items-center justify-center">
              {/* Logo */}
              <div className="relative space-y-2 w-full">
                <div className="text-sm font-semibold text-chestnut-rose mb-1 font-CanelaDeck text-center">
                  Logo
                </div>
                <div className="relative w-full max-w-[240px] mx-auto rounded-2xl overflow-hidden bg-white flex flex-col items-center p-1 justify-center border border-shades-05/50">
                  <div className="w-full h-40 p-1">
                    <Image
                      src={
                        values?.logo instanceof File
                          ? URL.createObjectURL(values.logo)
                          : values?.logo
                      }
                      alt="Logo"
                      classes="w-full h-full object-contain"
                    />
                  </div>
                  <div className="relative w-full">
                    <ButtonFile
                      buttonLabel="Change Logo"
                      buttonClasses="justify-center !rounded-xl"
                      onChange={(e) => {
                        const isValid = validateImage(e);
                        if (isValid) {
                          setFieldsValue({ logo: e.target.files[0] });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Favicon */}
              <div className="relative space-y-2 w-full">
                <div className="text-sm font-semibold text-chestnut-rose mb-1 font-CanelaDeck text-center">
                  Favicon
                </div>
                <div className="relative w-full max-w-[240px] mx-auto rounded-2xl overflow-hidden bg-white flex flex-col items-center p-1 justify-center border border-shades-05/50">
                  <div className="w-full h-40 p-1">
                    <Image
                      src={
                        values?.favicon instanceof File
                          ? URL.createObjectURL(values.favicon)
                          : values?.favicon
                      }
                      alt="Favicon"
                      classes="w-full h-full object-contain"
                    />
                  </div>
                  <div className="relative w-full">
                    <ButtonFile
                      buttonLabel="Change Favicon"
                      buttonClasses="justify-center !rounded-xl"
                      onChange={(e) => {
                        const isValid = validateImage(e);
                        if (isValid) {
                          setFieldsValue({ favicon: e.target.files[0] });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
